import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer, startViewTransition } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/*
 * Mengen und Entfernen im Warenkorb (Jimmi, 29.10.2026: "Im Warenkorb kann man
 * immer nur einen Artikel vermehren oder loeschen. Erst wenn die Aktion eines
 * Artikels vollendet ist, laesst es sich bei einem weiteren machen.")
 *
 * Vorher sperrte jede Aenderung den ganzen Warenkorb (.cart-items-disabled mit
 * pointer-events: none bis zur Antwort), und ein gemeinsamer Entpreller von
 * 300 ms liess die Aenderung einer anderen Zeile die vorige verschlucken.
 *
 * Jetzt ist jede Zeile fuer sich bedienbar:
 *  - Die Anzeige folgt sofort: der Zaehler zeigt die neue Menge, ein entfernter
 *    Posten klappt zu. Nur die betroffene Zeile zeigt ihren Ladezustand (Preis
 *    im Shimmer, dazu die Gesamtsumme); nichts wird gesperrt.
 *  - Wuensche werden je Zeile gesammelt (die letzte Menge gewinnt) und nach
 *    einer kurzen Buendelungszeit als EINE Anfrage gesendet (/cart/update.js
 *    mit den Schluesseln der Zeilen, nicht mit Zeilennummern - die verschieben
 *    sich beim Entfernen). Nie laufen zwei Anfragen zugleich; was waehrend einer
 *    Anfrage dazukommt, geht danach als naechster Stapel hinaus.
 *  - Neu gezeichnet (morph) wird erst, wenn nichts mehr wartet. Zeilen mit
 *    offenem Wunsch tragen data-dp-offen; morph.js laesst sie unangetastet.
 *  - Scheitert ein Stapel, wird er zeilenweise einzeln wiederholt; nur die
 *    Zeilen, die dann noch scheitern, springen auf den letzten bestaetigten
 *    Stand zurueck - mit der Meldung des Shops.
 */

/** Wartezeit, in der weitere Klicks derselben und anderer Zeilen mit in den Stapel kommen (ms). */
const BUENDELZEIT = 300;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** Je Zeile (Schluessel), solange sie in Bewegung ist: die zuletzt bestaetigte Menge. @type {Map<string, number>} */
  #stand = new Map();
  /** Gewuenschte Mengen, die noch nicht gesendet sind. @type {Map<string, number>} */
  #wuensche = new Map();
  /** Der Stapel der laufenden Anfrage (Schluessel -> Menge). @type {Map<string, number> | null} */
  #unterwegs = null;
  /** @type {number | undefined} */
  #frist;
  #letzterWunsch = 0;
  /** Eine Aktualisierung von aussen kam, waehrend Wuensche offen waren - sie wird danach nachgeholt. */
  #nachzeichnen = false;
  /** Der Warenkorb wurde optimistisch durch den Leer-Zustand ersetzt. */
  #leerGezeigt = false;
  /** Beginn der Leistungsmessung des laufenden Durchgangs. @type {any} */
  #messung = null;

  get #beschaeftigt() {
    return this.#unterwegs !== null || this.#wuensche.size > 0;
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        // Offene Mengenwuensche haben Vorrang: danach wird nachgeholt, nichts ueberschrieben.
        if (this.#beschaeftigt) {
          this.#nachzeichnen = true;
          return;
        }
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        if (this.#beschaeftigt) {
          this.#nachzeichnen = true;
          return;
        }
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    // Ohne Entpreller auf dem Dokument: gebuendelt wird je Zeile in #wunsch.
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityEvent);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /* ------------------------------------------------------------ Zeilen */

  /**
   * Die Zeile zu einem Schluessel.
   * @param {string} key
   * @returns {HTMLTableRowElement | undefined}
   */
  #zeile(key) {
    for (const row of this.querySelectorAll('tr[data-key]')) {
      if (row instanceof HTMLTableRowElement && row.dataset.key === key) return row;
    }
    return undefined;
  }

  /**
   * Die zuletzt bestaetigte Menge einer Zeile.
   * @param {string} key
   */
  #bestaetigt(key) {
    const bekannt = this.#stand.get(key);
    if (bekannt !== undefined) return bekannt;
    const input = this.#zeile(key)?.querySelector('input[data-cart-quantity]');
    return input instanceof HTMLInputElement ? parseInt(input.defaultValue, 10) || 0 : 0;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {Event} event - The event.
   */
  #onQuantityEvent = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const row = event.target.closest('tr[data-key]');
    if (!(row instanceof HTMLTableRowElement)) return;

    const { quantity } = event.detail;
    if (!Number.isFinite(quantity)) return;

    if (quantity === 0) {
      this.onLineItemRemove(row);
      return;
    }

    this.#wunsch(row, quantity);
  };

  /**
   * Haelt den Wunsch einer Zeile fest und plant den Stapel.
   * @param {HTMLTableRowElement} row
   * @param {number} quantity
   */
  #wunsch(row, quantity) {
    const key = row.dataset.key ?? '';
    if (!key) return;

    if (!this.#stand.has(key)) {
      const input = row.querySelector('input[data-cart-quantity]');
      this.#stand.set(key, input instanceof HTMLInputElement ? parseInt(input.defaultValue, 10) || 0 : 0);
    }
    if (!this.#messung) this.#messung = cartPerformance.createStartingMarker('change:user-action');

    this.#wuensche.set(key, quantity);
    row.setAttribute('data-dp-offen', '');
    // Nur diese Zeile zeigt ihren Ladezustand, dazu die Summe.
    /** @type {TextComponent | null} */ (row.querySelector('text-component'))?.shimmer();
    this.refs.cartTotal?.shimmer();

    this.#letzterWunsch = Date.now();
    this.#plane();
  }

  /** Sendet, sobald die Buendelungszeit seit dem letzten Wunsch um ist - nie zwei Anfragen zugleich. */
  #plane() {
    if (this.#unterwegs || this.#wuensche.size === 0) return;
    window.clearTimeout(this.#frist);
    const warte = Math.max(0, BUENDELZEIT - (Date.now() - this.#letzterWunsch));
    this.#frist = window.setTimeout(() => this.#sende(), warte);
  }

  #sende() {
    this.#frist = undefined;
    if (this.#unterwegs) return;

    /** @type {Map<string, number>} */
    const stapel = new Map();
    for (const [key, menge] of this.#wuensche) {
      // Wieder bei der bestaetigten Menge angekommen (etwa Plus, dann Minus): nichts zu senden.
      if (menge !== this.#bestaetigt(key)) stapel.set(key, menge);
    }
    const verworfen = [...this.#wuensche.keys()].filter((key) => !stapel.has(key));
    this.#wuensche.clear();
    for (const key of verworfen) this.#zeileBeruhigen(key);

    if (stapel.size === 0) {
      this.#ruhe();
      return;
    }

    this.#unterwegs = stapel;
    this.#lauf(stapel);
  }

  /**
   * Eine Zeile ohne offenen Wunsch: Schutz und Ladezustand weg, Gedaechtnis leeren.
   * @param {string} key
   */
  #zeileBeruhigen(key) {
    const row = this.#zeile(key);
    row?.removeAttribute('data-dp-offen');
    if (row) resetShimmer(row);
    this.#stand.delete(key);
  }

  /** Alles erledigt: Gedaechtnis leeren, Ladezustand weg, Nachgeholtes zeichnen. */
  #ruhe() {
    for (const key of [...this.#stand.keys()]) this.#zeileBeruhigen(key);
    resetShimmer(this);
    for (const row of this.querySelectorAll('[data-dp-offen]')) row.removeAttribute('data-dp-offen');
    if (this.#messung) {
      cartPerformance.measureFromMarker(this.#messung);
      this.#messung = null;
    }
    if (this.#nachzeichnen) {
      this.#nachzeichnen = false;
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
    }
  }

  /**
   * Eine Anfrage fuer einen Stapel.
   * @param {Map<string, number>} stapel
   * @returns {Promise<{ ok: true, daten: any } | { ok: false, status: number, text: string }>}
   */
  async #anfrage(stapel) {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const [key, menge] of stapel) updates[key] = menge;

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const antwort = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body }));
      /** @type {any} */
      let daten = null;
      try {
        daten = await antwort.json();
      } catch {
        daten = null;
      }
      if (!antwort.ok || !daten || daten.errors || (daten.status && Number(daten.status) >= 400) || !daten.sections) {
        const text = daten ? (typeof daten.errors === 'string' ? daten.errors : daten.message || daten.description || '') : '';
        return { ok: false, status: antwort.status, text: String(text || '') };
      }
      return { ok: true, daten };
    } catch (error) {
      return { ok: false, status: 0, text: String(/** @type {any} */ (error)?.message || error) };
    }
  }

  /**
   * Sendet einen Stapel; scheitert er, werden die Zeilen einzeln wiederholt,
   * damit nur die wirklich abgelehnten zurueckspringen.
   * @param {Map<string, number>} stapel
   */
  async #lauf(stapel) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const alleEntfernt = [...stapel.values()].every((menge) => menge === 0);
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: alleEntfernt ? 'remove' : 'update',
        context: 'cart',
        lines: [...stapel].map(([id, quantity]) => ({ id, quantity })),
        promise: deferred.promise,
      })
    );

    /** @type {any} */
    let letzte = null;
    /** @type {{ key: string, status: number, text: string }[]} */
    const abgelehnt = [];
    /** @type {Set<string>} */
    const gelungen = new Set();

    const erst = await this.#anfrage(stapel);
    if (erst.ok) {
      letzte = erst.daten;
      for (const key of stapel.keys()) gelungen.add(key);
    } else if (stapel.size === 1) {
      const [key] = [...stapel.keys()];
      abgelehnt.push({ key: /** @type {string} */ (key), status: erst.status, text: erst.text });
    } else {
      for (const [key, menge] of stapel) {
        const einzeln = await this.#anfrage(new Map([[key, menge]]));
        if (einzeln.ok) {
          letzte = einzeln.daten;
          gelungen.add(key);
        } else {
          abgelehnt.push({ key, status: einzeln.status, text: einzeln.text });
        }
      }
    }

    // Bestaetigt ist, was die letzte erfolgreiche Antwort fuer die Zeile nennt.
    if (letzte) {
      /** @type {Map<string, number>} */
      const imKorb = new Map();
      for (const item of letzte.items ?? []) imKorb.set(item.key, item.quantity);
      for (const key of gelungen) this.#stand.set(key, imKorb.get(key) ?? 0);
      this.#updateQuantitySelectors(letzte);
    }
    this.#unterwegs = null;

    if (letzte) {
      /** @type {any} */
      let cart;
      try {
        cart = CartLinesUpdateEvent.createCartFromAjaxResponse(letzte);
      } catch (error) {
        // Ohne lesbaren Warenkorb im Ereignis laeuft der Durchgang trotzdem weiter.
        console.warn('[cart-items] Warenkorb fuer das Ereignis nicht lesbar:', error);
      }
      deferred.resolve({
        cart,
        detail: {
          sections: letzte.sections,
          items: letzte.items,
          itemCount: letzte.item_count ?? 0,
          source: 'cart-items-component',
          didError: abgelehnt.length > 0,
        },
      });
    } else {
      deferred.reject(new Error('Cart update failed'));
    }

    for (const fehler of abgelehnt) this.#zurueck(fehler.key);
    if (abgelehnt.length) this.#meldeFehler(abgelehnt);

    // Neue Wuensche, die schon wieder beim bestaetigten Stand stehen, zaehlen nicht.
    for (const [key, menge] of [...this.#wuensche]) {
      if (menge === this.#bestaetigt(key)) {
        this.#wuensche.delete(key);
        this.#zeileBeruhigen(key);
      }
    }

    if (this.#wuensche.size > 0) {
      // Es kommt noch ein Stapel: jetzt nicht neu zeichnen - die letzte Antwort zeichnet alles.
      this.#plane();
      return;
    }

    // Nichts wartet mehr: der Schutz faellt, die letzte Antwort zeichnet den Warenkorb.
    for (const row of this.querySelectorAll('[data-dp-offen]')) row.removeAttribute('data-dp-offen');
    if (this.#leerGezeigt && abgelehnt.length) {
      this.#leerGezeigt = false;
      this.#ruhe();
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
      return;
    }
    if (letzte?.sections?.[this.sectionId]) {
      await morphSection(this.sectionId, letzte.sections[this.sectionId], {
        mode: this.isDrawer ? 'hydration' : 'full',
      });
      this.#leerGezeigt = false;
      this.#updateCartQuantitySelectorButtonStates();
    }
    this.#ruhe();
  }

  /**
   * Eine abgelehnte Zeile springt weich auf den bestaetigten Stand zurueck,
   * sofern der Kunde sie nicht inzwischen erneut geaendert hat.
   * @param {string} key
   */
  #zurueck(key) {
    if (this.#wuensche.has(key)) return;
    const menge = this.#stand.get(key) ?? 0;
    const row = this.#zeile(key);
    if (!row) return;

    // Entfernt gewesen: die Zeile klappt wieder auf, samt ihrer verschachtelten Posten.
    const rows = [row, ...[...this.querySelectorAll('tr[data-parent-key]')].filter((r) => r instanceof HTMLElement && r.dataset.parentKey === key)];
    for (const r of rows) {
      if (!(r instanceof HTMLElement) || !r.hasAttribute('data-dp-weg') && !r.classList.contains('removing')) continue;
      r.classList.remove('removing');
      r.removeAttribute('data-dp-weg');
      r.style.removeProperty('display');
      if (!prefersReducedMotion()) {
        r.classList.add('adding');
        onAnimationEnd(r, () => r.classList.remove('adding'));
      }
    }

    const input = row.querySelector('input[data-cart-quantity]');
    if (input instanceof HTMLInputElement) {
      input.value = String(menge);
      const selector = input.closest('cart-quantity-selector-component');
      /** @type {any} */ (selector)?.updateButtonStates?.();
    }
    this.#zeileBeruhigen(key);
  }

  /**
   * Die Meldung des Shops - dieselbe Funktion wie bei den anderen Fehlern.
   * @param {{ key: string, status: number, text: string }[]} fehler
   */
  async #meldeFehler(fehler) {
    const erster = /** @type {{ key: string, status: number, text: string }} */ (fehler[0]);
    this.dispatchEvent(new CartErrorEvent({ error: erster.text || 'Cart update failed', code: 'INVALID' }));
    try {
      const [meldung, fehlertext] = await Promise.all([
        import('@theme/dp-meldung'),
        import('@theme/dp-fehlertext').catch(() => null),
      ]);
      /** @type {string[]} */
      let saetze = ['Die Änderung im Warenkorb konnte nicht übernommen werden. Bitte versuche es noch einmal.'];
      /** @type {string | undefined} */
      let leise;
      if (fehlertext) {
        const k = fehlertext.fehlerText(erster.text, { status: erster.status, ort: 'warenkorb' });
        if (k.neutral) leise = fehlertext.codeZeile(k.code);
        else saetze = [k.text];
      }
      meldung.melde('Das hat nicht geklappt', saetze, leise);
    } catch (error) {
      console.warn('[cart-items] Meldung nicht moeglich:', error);
    }
  }

  /**
   * Handles the line item removal.
   * @param {HTMLElement | Event | number} ziel - die Zeile, ein Ereignis aus ihr oder (alt) ihre Zeilennummer
   */
  onLineItemRemove(ziel) {
    /** @type {Element | null} */
    let row = null;
    if (typeof ziel === 'number') row = this.querySelectorAll('tr[data-key]')[ziel - 1] ?? null;
    else if (ziel instanceof Event) row = ziel.target instanceof Element ? ziel.target.closest('tr[data-key]') : null;
    else row = ziel;
    if (!(row instanceof HTMLTableRowElement) || row.classList.contains('removing')) return;

    const key = row.dataset.key ?? '';
    if (!key) return;

    if (!this.#stand.has(key)) {
      const input = row.querySelector('input[data-cart-quantity]');
      this.#stand.set(key, input instanceof HTMLInputElement ? parseInt(input.defaultValue, 10) || 0 : 0);
    }
    if (!this.#messung) this.#messung = cartPerformance.createStartingMarker('clear:user-action');

    this.#wuensche.set(key, 0);
    row.setAttribute('data-dp-offen', '');
    this.refs.cartTotal?.shimmer();
    this.#letzterWunsch = Date.now();

    const rowsToRemove = [
      row,
      // Get all nested lines of the row to remove
      ...[...this.querySelectorAll('tr[data-parent-key]')].filter((r) => r instanceof HTMLElement && r.dataset.parentKey === key),
    ];

    // Sind alle Posten weg, steht sofort der leere Warenkorb da.
    const alleZeilen = [...this.querySelectorAll('tr[data-key]')];
    const bleibenStehen = alleZeilen.filter((r) => !rowsToRemove.includes(/** @type {any} */ (r)) && !r.hasAttribute('data-dp-weg') && !r.classList.contains('removing'));
    const isEmptyCart = bleibenStehen.length === 0;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      this.#leerGezeigt = true;

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      this.#plane();
      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((r) => {
      if (!(r instanceof HTMLElement)) return;
      r.setAttribute('data-dp-offen', '');
      // Die Zeile bleibt zugeklappt im Dokument, bis der Warenkorb neu gezeichnet ist - so kann sie bei einem Fehler zurueckkehren.
      const weg = () => {
        if (!r.classList.contains('removing')) return;
        r.setAttribute('data-dp-weg', '');
        r.style.setProperty('display', 'none');
      };

      if (prefersReducedMotion()) {
        r.classList.add('removing');
        return weg();
      }

      r.style.setProperty('--row-height', `${r.clientHeight}px`);
      r.classList.add('removing');

      // Nach der Bewegung ganz aus dem Weg
      onAnimationEnd(r, weg);
    });

    this.#plane();
  }

  /**
   * Updates the quantity (Schnittstelle fuer Aufrufe von aussen).
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const row = this.querySelectorAll('tr[data-key]')[config.line - 1];
    if (!(row instanceof HTMLTableRowElement)) return;
    if (config.quantity === 0) this.onLineItemRemove(row);
    else this.#wunsch(row, config.quantity);
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        // Waehrend Mengenwuensche offen sind, wird nichts ueberzeichnet; danach holt #ruhe es nach.
        if (this.#beschaeftigt) {
          this.#nachzeichnen = true;
          return;
        }
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
